import { ApolloError } from '@apollo/client';
import { createContext, useContext, useEffect, useMemo, useState } from 'react';
import compact from 'lodash/compact';
import map from 'lodash/map';
import useGetLearningLifecyclesData from '@src/hooks/useGetLearningLifecyclesData';
import useGetProgramsByUuid from '@src/hooks/useGetProgramsByUuid';
import { LearningLifecyclesData } from '@src/graphql/home/queries';

interface LearningLifecyclesContextInterface {
  error?: ApolloError;
  loading?: boolean;
  learningLifecycles?: LearningLifecyclesData[];
  currentLearningLifecycle?: LearningLifecyclesData;
  setCurrentLearningLifecycle?: React.Dispatch<
    React.SetStateAction<LearningLifecyclesData | undefined>
  >;
}

const LearningLifecyclesContext = createContext<LearningLifecyclesContextInterface>({});

const LearningLifecyclesProvider: React.FC = ({ children }) => {
  const [learningLifecycles, setLearningLifecycles] = useState<
    LearningLifecyclesData[] | undefined
  >();
  const [currentLearningLifecycle, setCurrentLearningLifecycle] = useState<
    LearningLifecyclesData | undefined
  >();

  const { error, loading, learningLifecyclesData } = useGetLearningLifecyclesData();

  const programUuids = useMemo(() => compact(map(learningLifecyclesData, 'programUuid')), [
    learningLifecyclesData,
  ]);

  const { programData } = useGetProgramsByUuid(programUuids);

  useEffect(() => {
    const addProgramData = (lifecycle: LearningLifecyclesData) => ({
      ...lifecycle,
      program: programData?.find((program) => program.uuid === lifecycle.programUuid),
    });

    if (learningLifecyclesData) {
      const updatedLearningLifecycles = learningLifecyclesData.map(addProgramData);
      setLearningLifecycles(updatedLearningLifecycles);
    }
  }, [learningLifecyclesData, programData]);

  useEffect(() => {
    if (learningLifecycles?.length) {
      setCurrentLearningLifecycle(learningLifecycles?.[0]);
    }
  }, [learningLifecycles]);

  return (
    <LearningLifecyclesContext.Provider
      value={{
        error,
        loading,
        learningLifecycles,
        currentLearningLifecycle,
        setCurrentLearningLifecycle,
      }}
    >
      {children}
    </LearningLifecyclesContext.Provider>
  );
};

const useLearningLifecyclesContext = () => useContext(LearningLifecyclesContext);

export default LearningLifecyclesProvider;

export {
  useLearningLifecyclesContext,
  LearningLifecyclesContext,
  LearningLifecyclesProvider,
  LearningLifecyclesContextInterface,
};
